import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { ApiError } from '@/api/errors'
import type { DayAvailabilityResponse, Paginated, TravelPlanResponse } from '@/api/types'
import { queryKeys } from '@/api/queryKeys'
import { Banner } from '@/components/Banner'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { Page, PageHeader } from '@/components/Page'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { SkeletonList } from '@/components/Skeleton'
import { DateField } from '@/components/DateField'
import { useToast } from '@/components/Toast'
import { formatDateRange, todayIso } from '@/lib/dates'
import { travel as api } from '@/features/callers/api'
import { appointments as appointmentsApi } from '@/features/appointments/api'
import { useModals } from '@/app/modals/ModalContext'
import { errorMessage } from '@/api/messages'

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}


/** Where the doctor is sitting, day by day — the other half of an enquiry's
 *  "preferred location". */
export function TravelScreen() {
  const toast = useToast()
  const modals = useModals()
  const queryClient = useQueryClient()
  const [from, setFrom] = useState(() => addDays(todayIso(), -7))
  const [to, setTo] = useState(() => addDays(todayIso(), 60))

  const plans = useQuery<Paginated<TravelPlanResponse>, ApiError>({
    queryKey: queryKeys.travel(from, to),
    queryFn: ({ signal }) => api.list(from, to, signal),
  })

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['travel'] })
    await queryClient.invalidateQueries({ queryKey: ['calendar'] })
  }


  // Held rather than removed on the spot: a travel day is what the clinic told
  // patients, and there is no undo.
  const [pending, setPending] = useState<TravelPlanResponse | null>(null)

  const remove = useMutation<void, ApiError, string>({
    mutationFn: (id) => api.remove(id),
    onSuccess: async () => {
      await refresh()
      setPending(null)
      toast.show('Travel day removed')
    },
    onError: (err) => toast.showError(errorMessage(err)),
  })

  /** Whether anyone is already booked on the day being removed. Removing the
   *  day the doctor sits somewhere, with patients expecting them, is the
   *  mistake worth a sentence of warning. */
  const affected = useQuery<Paginated<DayAvailabilityResponse>, ApiError>({
    queryKey: ['availability', pending?.travel_date],
    queryFn: ({ signal }) =>
      appointmentsApi.availability(pending!.travel_date, pending!.travel_date, signal),
    enabled: Boolean(pending),
  })
  const bookedThatDay = affected.data?.data[0]?.booked ?? 0

  const rows = plans.data?.data ?? []

  const add = (
    <Button onClick={() => modals.open({ kind: 'travel' })}>
      <Icon name="plus" />
      Add travel
    </Button>
  )

  return (
    <Page fill action={add}>
      <PageHeader
        eyebrow="Booking"
        title="Travel"
        description="Which place the doctor is at, and when."
        primary={add}
      >
        {/* The window is a filter, not an action: below the title with the rest
            of the screen's controls, and two across on a phone rather than two
            full-width fields stacked. */}
        <div className="grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
          <DateField label="From" value={from} onChange={setFrom} />
          <DateField label="To" value={to} onChange={setTo} />
        </div>
      </PageHeader>

      {plans.error && <Banner tone="danger">{errorMessage(plans.error)}</Banner>}

      <Card title={`${rows.length} ${rows.length === 1 ? 'trip' : 'trips'} planned`} flush fill>
          {plans.isLoading ? (
            <div className="p-5">
              <SkeletonList rows={4} label="Loading travel plan" />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<Icon name="pin" size="2rem" />}
              title="Nothing planned in this window"
              body="Add the days the doctor sits at each place — a single day or a run of them — and appointments can be booked against them."
            />
          ) : (
            <ul className="m-0 list-none p-0 [&>li+li]:border-t [&>li+li]:border-line-soft">
              {rows.map((plan) => (
                <li className="flex min-h-touch items-center gap-3 px-4 py-3 sm:gap-4" key={plan.id}>
                  {/* Above the place on a phone, beside it from `sm` up: 160px
                      of date column left a hill station's name four characters
                      wide on a 360px screen. */}
                  <span className="flex-none text-sm text-fg-muted tabular-nums max-sm:hidden sm:w-40">
                    {formatDateRange(plan.travel_date, plan.end_date)}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="text-xs text-fg-muted tabular-nums sm:hidden">
                      {formatDateRange(plan.travel_date, plan.end_date)}
                    </span>
                    <strong>{plan.location}</strong>
                    {(plan.depart_at || plan.return_at) && (
                      <span className="text-xs text-fg-subtle tabular-nums">
                        {(plan.depart_at ?? '—').slice(0, 5)} – {(plan.return_at ?? '—').slice(0, 5)}
                      </span>
                    )}
                    {plan.notes && <span className="text-xs text-fg-subtle">{plan.notes}</span>}
                  </span>
                  <Button
                    variant="ghost"
                    size="small"
                    aria-label={`Remove ${plan.location} on ${formatDateRange(plan.travel_date, plan.end_date)}`}
                    onClick={() => setPending(plan)}
                  >
                    <Icon name="trash" size="1rem" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
      </Card>

      <ConfirmDialog
        open={Boolean(pending)}
        title="Remove this travel day?"
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => pending && remove.mutate(pending.id)}
        onCancel={() => setPending(null)}
      >
        {pending && (
          <>
            <p>
              <strong className="font-semibold text-fg">{pending.location}</strong> on{' '}
              <strong className="font-semibold text-fg">
                {formatDateRange(pending.travel_date, pending.end_date)}
              </strong>{' '}
              will no longer show as a day the doctor is sitting there. This cannot be undone.
            </p>
            {bookedThatDay > 0 && (
              <p className="mt-3 flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-warning">
                <Icon name="alert" size="1rem" className="mt-[0.1em] flex-none" />
                <span>
                  {bookedThatDay === 1
                    ? '1 patient is already booked that day.'
                    : `${bookedThatDay} patients are already booked that day.`}{' '}
                  Their appointments stay, but the day will show no location.
                </span>
              </p>
            )}
          </>
        )}
      </ConfirmDialog>
    </Page>
  )
}
